// absolute imports
import { Autocomplete, Box, InputAdornment, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

// relative imports
import { VirtualizedListboxComponent } from '../custom-components/AutoCompleteTextField';
import PopperVirtualized from '../custom-components/LedgiblePopper';
import Loader from '../custom-components/Loader';

const useVirtualizedStyles = makeStyles()({
  listbox: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

const FormMultiAutoCompleteField = ({
  input: { onChange, onFocus, onBlur, value },
  meta: { error, touched, pristine, submitFailed },
  optionList,
  getOptionLabel,
  isOptionEqualToValue,
  noOptionsText,
  fetching,
  sortBy,
  virtualize,
  TextFieldProps,
  VirtualizedProps,
  'data-cy': cypressId,
  ...restAutoCompleteProps
}) => {
  const { classes: virtualizedClasses } = useVirtualizedStyles();
  const [inputValue, setInputValue] = useState('');

  const containsError = !!(
    (touched && error && !pristine) ||
    (error && submitFailed)
  );

  const getHelperText = () => {
    if (containsError) return error;
    if (TextFieldProps?.helperText) return TextFieldProps.helperText;
    return null;
  };

  const sortedOptions = sortBy ? [...optionList].sort(sortBy) : optionList;

  // Virtualization props
  const virtualizedProps = virtualize
    ? {
        classes: virtualizedClasses,
        disableListWrap: true,
        ListboxComponent: VirtualizedListboxComponent,
        ListboxProps: {
          VirtualizedProps,
        },
        PopperComponent: PopperVirtualized,
      }
    : {};

  const handleChange = (event, newValue) => {
    onChange(newValue);
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const handleBlur = (event) => {
    setInputValue('');
    onBlur(event);
  };

  return (
    <Autocomplete
      multiple
      data-cy={cypressId}
      options={sortedOptions}
      value={value || []}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      noOptionsText={noOptionsText}
      filterSelectedOptions
      disableCloseOnSelect
      loading={fetching}
      {...virtualizedProps}
      renderInput={(params) => (
        <TextField
          {...params}
          {...TextFieldProps}
          error={containsError}
          helperText={getHelperText()}
          onFocus={onFocus}
          onBlur={handleBlur}
          FormHelperTextProps={{
            style: {
              whiteSpace: 'pre-line',
            },
          }}
          InputProps={{
            ...params.InputProps,
            ...TextFieldProps?.InputProps,
            endAdornment: (
              <>
                {fetching && (
                  <InputAdornment position="end">
                    <Box mt={-1}>
                      <Loader size={20} thickness={6} />
                    </Box>
                  </InputAdornment>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          InputLabelProps={{
            shrink: true,
            ...TextFieldProps?.InputLabelProps,
          }}
        />
      )}
      {...restAutoCompleteProps}
    />
  );
};

export default FormMultiAutoCompleteField;

FormMultiAutoCompleteField.propTypes = {
  input: PropTypes.shape().isRequired,
  meta: PropTypes.shape().isRequired,
  optionList: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.shape()]),
  ).isRequired,
  getOptionLabel: PropTypes.func,
  isOptionEqualToValue: PropTypes.func,
  noOptionsText: PropTypes.string,
  fetching: PropTypes.bool,
  sortBy: PropTypes.func,
  virtualize: PropTypes.bool,
  TextFieldProps: PropTypes.shape(),
  VirtualizedProps: PropTypes.shape(),
  'data-cy': PropTypes.string,
};

FormMultiAutoCompleteField.defaultProps = {
  getOptionLabel: (option) =>
    typeof option === 'string' ? option : option.name || '',
  isOptionEqualToValue: (option, value) => option?.id === value?.id,
  noOptionsText: 'No options',
  fetching: false,
  sortBy: null,
  virtualize: false,
  TextFieldProps: {},
  VirtualizedProps: {},
  'data-cy': undefined,
};
